import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  articleTags?: string[];
  schema?: Record<string, unknown>;
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogType = 'website',
  articlePublishedTime,
  articleModifiedTime,
  articleTags,
  schema,
}: SEOHeadProps) {
  useEffect(() => {
    if (typeof document === 'undefined') {
      return null;
    }

    document.title = title;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);
    if (ogTitle) setMeta('og:title', ogTitle, true);
    if (ogDescription) setMeta('og:description', ogDescription, true);
    setMeta('og:type', ogType, true);
    if (canonicalUrl) {
      setMeta('og:url', canonicalUrl, true);
      let linkEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!linkEl) {
        linkEl = document.createElement('link');
        linkEl.setAttribute('rel', 'canonical');
        document.head.appendChild(linkEl);
      }
      linkEl.setAttribute('href', canonicalUrl);
    }
    setMeta('twitter:card', 'summary_large_image');
    if (ogTitle) setMeta('twitter:title', ogTitle);
    if (ogDescription) setMeta('twitter:description', ogDescription);

    if (articlePublishedTime) setMeta('article:published_time', articlePublishedTime);
    if (articleModifiedTime) setMeta('article:modified_time', articleModifiedTime);
    if (articleTags && articleTags.length > 0) {
      articleTags.forEach((tag) => {
        let tagEl = document.querySelector(`meta[property="article:tag"][content="${tag}"]`);
        if (!tagEl) {
          tagEl = document.createElement('meta');
          tagEl.setAttribute('property', 'article:tag');
          tagEl.setAttribute('content', tag);
          document.head.appendChild(tagEl);
        }
      });
    }

    if (schema) {
      let scriptEl = document.getElementById('schema-dynamic') as HTMLScriptElement | null;
      if (!scriptEl) {
        scriptEl = document.createElement('script');
        scriptEl.type = 'application/ld+json';
        scriptEl.id = 'schema-dynamic';
        document.head.appendChild(scriptEl);
      }
      scriptEl.textContent = JSON.stringify(schema);
    }

    return () => {
      const dynamicScript = document.getElementById('schema-dynamic');
      if (dynamicScript) dynamicScript.remove();
    };
  }, [title, description, keywords, canonicalUrl, ogTitle, ogDescription, ogType, articlePublishedTime, articleModifiedTime, articleTags, schema]);

  return null;
}